import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Loader2, Plus, Upload, Archive, ArchiveRestore, Search, Check,
  CalendarDays, CreditCard, Mail, NotebookPen, FileText, ChevronDown, ChevronUp,
} from "lucide-react";
import { API, fmtDate } from "./shared";

const inputCls =
  "bg-white border border-[#BCB3A6]/50 rounded-2xl px-3 py-2 text-sm text-[#3F5043] focus:outline-none focus:ring-2 focus:ring-[#899480] w-full";

const CLIENT_STATUSES = ["active", "inactive", "prospect", "completed"];
const LANGS = ["English", "Haitian Creole", "French", "Spanish", "Other"];

const EMPTY_FORM = {
  first_name: "", last_name: "", email: "", phone: "", date_of_birth: "",
  partner_name: "", preferred_language: "English", clinic_name: "",
  journey_stage: "", subscription_plan: "", status: "prospect", notes: "",
};

const EMPTY_INTAKE = {
  ...EMPTY_FORM,
  status: "active",
  address: "", referral_source: "", emergency_contact_name: "", emergency_contact_phone: "",
  ttc_duration: "", previous_treatments: "", medical_notes: "",
};

export function ClientsTab({ authHeader }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [mode, setMode] = useState(null); // null | add | import
  const [form, setForm] = useState(EMPTY_FORM);
  const [csvText, setCsvText] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [openEmail, setOpenEmail] = useState(null); // email of expanded client
  const [editTarget, setEditTarget] = useState(null); // client being edited

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/clients`, { headers: authHeader() });
      setClients(res.data);
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setMsg("");
    try {
      await axios.post(`${API}/admin/clients`, form, { headers: authHeader() });
      setForm(EMPTY_FORM);
      setMode(null);
      setMsg("Client added.");
      await load();
    } catch (e2) {
      setMsg(e2?.response?.data?.detail || "Could not add the client.");
    } finally { setBusy(false); }
  };

  const createIntake = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setMsg("");
    try {
      await axios.post(`${API}/admin/clients`, form, { headers: authHeader() });
      setForm(EMPTY_INTAKE);
      setMode(null);
      setMsg("Intake complete — client added to your list. Share the portal access code with them whenever they're ready.");
      await load();
    } catch (e2) {
      setMsg(e2?.response?.data?.detail || "Could not save the intake.");
    } finally { setBusy(false); }
  };

  const importCsv = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setMsg("");
    try {
      const res = await axios.post(`${API}/admin/clients/import`, { csv_text: csvText }, { headers: authHeader() });
      const d = res.data;
      setMsg(`Imported ${d.created} · skipped ${d.skipped}${d.errors.length ? ` · ${d.errors.length} row errors` : ""}`);
      setCsvText("");
      setMode(null);
      await load();
    } catch (e2) {
      setMsg(e2?.response?.data?.detail || "Import failed.");
    } finally { setBusy(false); }
  };

  const toggleArchive = async (c) => {
    await axios.post(`${API}/admin/clients/${encodeURIComponent(c.email)}/${c.archived ? "reactivate" : "archive"}`, {}, { headers: authHeader() }).catch(() => {});
    load();
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (busy || !editTarget) return;
    setBusy(true);
    try {
      await axios.patch(`${API}/admin/clients/${encodeURIComponent(editTarget.email)}/manage`, form, { headers: authHeader() });
      setEditTarget(null);
      setMsg("Client updated.");
      await load();
    } catch (e2) {
      setMsg(e2?.response?.data?.detail || "Update failed.");
    } finally { setBusy(false); }
  };

  const startEdit = (c) => {
    const p = c.profile || {};
    const parts = (c.full_name || "").split(" ");
    setForm({
      first_name: p.first_name || parts[0] || "",
      last_name: p.last_name || parts.slice(1).join(" ") || "",
      email: c.email,
      phone: p.phone || "",
      date_of_birth: p.date_of_birth || "",
      partner_name: p.partner_name || "",
      preferred_language: p.preferred_language || "English",
      clinic_name: p.clinic_name || "",
      journey_stage: p.journey_stage || "",
      subscription_plan: p.subscription_plan || c.membership_status || "",
      status: c.client_status || (c.access_granted ? "active" : "prospect"),
      notes: p.admin_notes || "",
    });
    setEditTarget(c);
  };

  const q = search.trim().toLowerCase();
  const visible = clients.filter((c) => {
    if (!showArchived && c.archived) return false;
    if (statusFilter && (c.client_status || "prospect") !== statusFilter) return false;
    if (q && !`${c.full_name} ${c.email} ${c.profile?.clinic_name || ""}`.toLowerCase().includes(q)) return false;
    return true;
  });

  const clientForm = (onSubmit, submitLabel, testid) => (
    <form onSubmit={onSubmit} data-testid={testid}
      className="bg-white border border-[#BCB3A6]/40 rounded-3xl p-5 mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      <input required placeholder="First name" value={form.first_name} data-testid="cf-first"
        onChange={(e) => setForm({ ...form, first_name: e.target.value })} className={inputCls} />
      <input placeholder="Last name" value={form.last_name} data-testid="cf-last"
        onChange={(e) => setForm({ ...form, last_name: e.target.value })} className={inputCls} />
      <input required={!editTarget} type="email" placeholder="Email" value={form.email} disabled={!!editTarget}
        data-testid="cf-email" onChange={(e) => setForm({ ...form, email: e.target.value })}
        className={`${inputCls} ${editTarget ? "opacity-60" : ""}`} />
      <input placeholder="Phone" value={form.phone} data-testid="cf-phone"
        onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
      <input type="date" placeholder="Date of birth" value={form.date_of_birth} data-testid="cf-dob"
        onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} className={inputCls} />
      <input placeholder="Partner name" value={form.partner_name} data-testid="cf-partner"
        onChange={(e) => setForm({ ...form, partner_name: e.target.value })} className={inputCls} />
      <select value={form.preferred_language} data-testid="cf-language"
        onChange={(e) => setForm({ ...form, preferred_language: e.target.value })} className={inputCls}>
        {LANGS.map((l) => <option key={l}>{l}</option>)}
      </select>
      <input placeholder="Clinic name" value={form.clinic_name} data-testid="cf-clinic"
        onChange={(e) => setForm({ ...form, clinic_name: e.target.value })} className={inputCls} />
      <input placeholder="Journey stage (e.g. IVF — Stimulation)" value={form.journey_stage} data-testid="cf-stage"
        onChange={(e) => setForm({ ...form, journey_stage: e.target.value })} className={inputCls} />
      <input placeholder="Subscription plan" value={form.subscription_plan} data-testid="cf-plan"
        onChange={(e) => setForm({ ...form, subscription_plan: e.target.value })} className={inputCls} />
      <select value={form.status} data-testid="cf-status"
        onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls}>
        {CLIENT_STATUSES.map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
      </select>
      <textarea placeholder="Custom notes" value={form.notes} data-testid="cf-notes" rows={2}
        onChange={(e) => setForm({ ...form, notes: e.target.value })}
        className={`${inputCls} sm:col-span-2 lg:col-span-3 resize-none`} />
      <div className="sm:col-span-2 lg:col-span-3 flex gap-3">
        <button type="submit" data-testid={`${testid}-submit`} disabled={busy}
          className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 bg-[#3F5043] text-[#F8F7F3] text-xs uppercase tracking-[0.18em] font-semibold hover:bg-[#875F45] transition-colors disabled:opacity-60">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} {submitLabel}
        </button>
        <button type="button" onClick={() => { setMode(null); setEditTarget(null); }}
          className="text-xs uppercase tracking-[0.15em] text-[#899480] hover:text-[#3F5043]">
          Cancel
        </button>
      </div>
    </form>
  );

  return (
    <div data-testid="clients-tab">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="font-serif text-2xl text-[#3F5043]">Client Management</div>
          <p className="text-sm text-[#3F5043]/70">Add, import, edit, archive — every client in one place.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => { setMode("intake"); setEditTarget(null); setForm(EMPTY_INTAKE); }} data-testid="client-intake-open"
            className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-[#D4AF37] text-[#3F5043] text-xs uppercase tracking-[0.15em] font-semibold hover:bg-[#3F5043] hover:text-[#F8F7F3] transition-colors">
            <NotebookPen size={13} /> Intake form
          </button>
          <button onClick={() => { setMode("add"); setEditTarget(null); setForm(EMPTY_FORM); }} data-testid="client-add-open"
            className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-[#3F5043] text-[#F8F7F3] text-xs uppercase tracking-[0.15em] font-semibold hover:bg-[#875F45] transition-colors">
            <Plus size={13} /> Add client
          </button>
          <button onClick={() => setMode("import")} data-testid="client-import-open"
            className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 border border-[#899480]/50 text-[#3F5043] text-xs uppercase tracking-[0.15em] font-semibold hover:bg-[#899480]/10 transition-colors">
            <Upload size={13} /> Import CSV
          </button>
        </div>
      </div>

      {msg && <div className="mb-4 bg-[#899480]/10 border border-[#899480]/30 rounded-2xl p-3 text-sm text-[#3F5043]" data-testid="clients-msg">{msg}</div>}

      {mode === "add" && clientForm(create, "Add client", "client-add-form")}
      {mode === "intake" && (
        <form onSubmit={createIntake} data-testid="client-intake-form"
          className="bg-white border border-[#D4AF37]/40 rounded-3xl p-6 mb-6">
          <div className="font-serif text-xl text-[#3F5043] mb-1">New client intake</div>
          <p className="text-xs text-[#899480] mb-5">
            Complete this with the client — perfect for walk-ins and clients who aren't comfortable with technology.
            Fields marked * are required; everything else can be filled later.
          </p>

          <div className="text-[10px] uppercase tracking-[0.2em] text-[#899480] font-semibold mb-2">Contact</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
            <input required placeholder="First name *" value={form.first_name} data-testid="intake-first"
              onChange={(e) => setForm({ ...form, first_name: e.target.value })} className={inputCls} />
            <input placeholder="Last name" value={form.last_name} data-testid="intake-last"
              onChange={(e) => setForm({ ...form, last_name: e.target.value })} className={inputCls} />
            <input required type="email" placeholder="Email *" value={form.email} data-testid="intake-email"
              onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
            <input placeholder="Phone" value={form.phone} data-testid="intake-phone"
              onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
            <div>
              <label className="block text-[10px] uppercase tracking-[0.15em] text-[#899480] mb-1">Date of birth</label>
              <input type="date" value={form.date_of_birth} data-testid="intake-dob"
                onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} className={`${inputCls} w-full`} />
            </div>
            <select value={form.preferred_language} data-testid="intake-language"
              onChange={(e) => setForm({ ...form, preferred_language: e.target.value })} className={inputCls}>
              {LANGS.map((l) => <option key={l}>{l}</option>)}
            </select>
            <input placeholder="Address" value={form.address} data-testid="intake-address"
              onChange={(e) => setForm({ ...form, address: e.target.value })} className={`${inputCls} lg:col-span-2`} />
          </div>

          <div className="text-[10px] uppercase tracking-[0.2em] text-[#899480] font-semibold mb-2">Partner & emergency contact</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
            <input placeholder="Partner name" value={form.partner_name} data-testid="intake-partner"
              onChange={(e) => setForm({ ...form, partner_name: e.target.value })} className={inputCls} />
            <input placeholder="Emergency contact name" value={form.emergency_contact_name} data-testid="intake-emergency-name"
              onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })} className={inputCls} />
            <input placeholder="Emergency contact phone" value={form.emergency_contact_phone} data-testid="intake-emergency-phone"
              onChange={(e) => setForm({ ...form, emergency_contact_phone: e.target.value })} className={inputCls} />
          </div>

          <div className="text-[10px] uppercase tracking-[0.2em] text-[#899480] font-semibold mb-2">Fertility journey</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
            <input placeholder="Clinic name" value={form.clinic_name} data-testid="intake-clinic"
              onChange={(e) => setForm({ ...form, clinic_name: e.target.value })} className={inputCls} />
            <input placeholder="Journey stage (e.g. IVF — Stimulation)" value={form.journey_stage} data-testid="intake-stage"
              onChange={(e) => setForm({ ...form, journey_stage: e.target.value })} className={inputCls} />
            <input placeholder="How did they hear about us?" value={form.referral_source} data-testid="intake-referral"
              onChange={(e) => setForm({ ...form, referral_source: e.target.value })} className={inputCls} />
            <input placeholder="How long trying to conceive? (e.g. 2 years)" value={form.ttc_duration} data-testid="intake-ttc"
              onChange={(e) => setForm({ ...form, ttc_duration: e.target.value })} className={inputCls} />
            <select value={form.subscription_plan} data-testid="intake-plan"
              onChange={(e) => setForm({ ...form, subscription_plan: e.target.value })} className={inputCls}>
              <option value="">Subscription plan (if any)…</option>
              <option value="Premium">Premium ($59.99/mo)</option>
              <option value="IUI Support">IUI Support</option>
              <option value="IVF Support">IVF Support</option>
              <option value="Consulting">Consulting client</option>
            </select>
            <select value={form.status} data-testid="intake-status"
              onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls}>
              {CLIENT_STATUSES.map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
            </select>
            <textarea placeholder="Previous treatments (IUI, IVF, surgeries…)" value={form.previous_treatments} rows={2}
              data-testid="intake-previous"
              onChange={(e) => setForm({ ...form, previous_treatments: e.target.value })}
              className={`${inputCls} resize-none sm:col-span-2 lg:col-span-3`} />
          </div>

          <div className="text-[10px] uppercase tracking-[0.2em] text-[#899480] font-semibold mb-2">Notes</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
            <textarea placeholder="Medical notes (allergies, conditions…)" value={form.medical_notes} rows={2}
              data-testid="intake-medical"
              onChange={(e) => setForm({ ...form, medical_notes: e.target.value })} className={`${inputCls} resize-none`} />
            <textarea placeholder="Team notes" value={form.notes} rows={2} data-testid="intake-notes"
              onChange={(e) => setForm({ ...form, notes: e.target.value })} className={`${inputCls} resize-none`} />
          </div>

          <div className="flex gap-3">
            <button type="submit" data-testid="intake-submit" disabled={busy}
              className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 bg-[#3F5043] text-[#F8F7F3] text-xs uppercase tracking-[0.18em] font-semibold hover:bg-[#875F45] transition-colors disabled:opacity-60">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Complete intake
            </button>
            <button type="button" onClick={() => setMode(null)} className="text-xs uppercase tracking-[0.15em] text-[#899480]">Cancel</button>
          </div>
        </form>
      )}
      {editTarget && clientForm(saveEdit, "Save changes", "client-edit-form")}
      {mode === "import" && (
        <form onSubmit={importCsv} data-testid="client-import-form"
          className="bg-white border border-[#BCB3A6]/40 rounded-3xl p-5 mb-6">
          <p className="text-xs text-[#899480] mb-2">
            Paste CSV with columns: first_name, last_name, email, phone, clinic_name, status
          </p>
          <textarea rows={6} value={csvText} data-testid="client-import-text"
            onChange={(e) => setCsvText(e.target.value)}
            placeholder={"first_name,last_name,email,phone,clinic_name,status\nJane,Doe,jane@example.com,4075551234,Orlando Fertility,active"}
            className={`${inputCls} font-mono text-xs resize-y`} />
          <div className="flex gap-3 mt-3">
            <button type="submit" data-testid="client-import-submit" disabled={busy}
              className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 bg-[#3F5043] text-[#F8F7F3] text-xs uppercase tracking-[0.18em] font-semibold hover:bg-[#875F45] transition-colors disabled:opacity-60">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Import
            </button>
            <button type="button" onClick={() => setMode(null)} className="text-xs uppercase tracking-[0.15em] text-[#899480]">Cancel</button>
          </div>
        </form>
      )}

      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#899480]" />
          <input placeholder="Search name, email, clinic…" value={search} data-testid="clients-search"
            onChange={(e) => setSearch(e.target.value)} className={`${inputCls} pl-9 w-64`} />
        </div>
        <select value={statusFilter} data-testid="clients-status-filter"
          onChange={(e) => setStatusFilter(e.target.value)} className={`${inputCls} w-40`}>
          <option value="">All statuses</option>
          {CLIENT_STATUSES.map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
        </select>
        <label className="flex items-center gap-2 text-xs text-[#899480]">
          <input type="checkbox" checked={showArchived} data-testid="clients-show-archived"
            onChange={(e) => setShowArchived(e.target.checked)} className="accent-[#3F5043]" />
          Show archived
        </label>
      </div>

      {loading ? (
        <div className="text-center py-10"><Loader2 size={18} className="animate-spin inline text-[#899480]" /></div>
      ) : visible.length === 0 ? (
        <div className="bg-white rounded-2xl border border-[#BCB3A6]/40 p-8 text-center text-sm text-[#899480]" data-testid="clients-empty">
          No clients match.
        </div>
      ) : (
        <div className="space-y-2" data-testid="clients-list">
          {visible.map((c) => (
            <div key={c.email} className="bg-white border border-[#BCB3A6]/40 rounded-2xl px-4 py-3" data-testid={`client-row-${c.email}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <button onClick={() => setOpenEmail(openEmail === c.email ? null : c.email)} className="text-left min-w-0"
                  data-testid={`client-open-${c.email}`}>
                  <div className="text-sm font-medium text-[#3F5043] flex items-center gap-2 flex-wrap">
                    {c.full_name || c.email}
                    <span className={`text-[9px] uppercase tracking-[0.12em] font-bold px-2 py-0.5 rounded-full ${
                      (c.client_status || "prospect") === "active" ? "bg-[#899480]/15 text-[#3F5043]"
                      : (c.client_status || "prospect") === "completed" ? "bg-[#3F5043]/10 text-[#3F5043]"
                      : "bg-[#D4AF37]/15 text-[#875F45]"}`}>
                      {c.archived ? "Archived" : (c.client_status || "prospect")}
                    </span>
                  </div>
                  <div className="text-xs text-[#899480]">
                    {c.email}{c.profile?.clinic_name ? ` · ${c.profile.clinic_name}` : ""}
                    {c.profile?.journey_stage ? ` · ${c.profile.journey_stage}` : ""}
                  </div>
                </button>
                <div className="flex items-center gap-2">
                  <button onClick={() => startEdit(c)} data-testid={`client-edit-${c.email}`}
                    className="text-xs rounded-full px-3 py-1.5 border border-[#899480]/50 text-[#3F5043] hover:bg-[#899480]/10 transition-colors">
                    Edit
                  </button>
                  <button onClick={() => toggleArchive(c)} data-testid={`client-archive-${c.email}`}
                    className="inline-flex items-center gap-1.5 text-xs rounded-full px-3 py-1.5 border border-[#875F45]/40 text-[#875F45] hover:bg-[#875F45]/5 transition-colors">
                    {c.archived ? <ArchiveRestore size={12} /> : <Archive size={12} />}
                    {c.archived ? "Reactivate" : "Archive"}
                  </button>
                  {openEmail === c.email ? <ChevronUp size={14} className="text-[#899480]" /> : <ChevronDown size={14} className="text-[#899480]" />}
                </div>
              </div>
              {openEmail === c.email && <ClientHistory email={c.email} authHeader={authHeader} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ClientHistory({ email, authHeader }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/clients/${encodeURIComponent(email)}/history`, { headers: authHeader() })
      .then((r) => setData(r.data))
      .catch(() => setData({ appointments: [], invoices: [], emails: [], consulting_notes: [], admin_notes: [] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  if (!data) return <div className="py-4 text-center"><Loader2 size={16} className="animate-spin inline text-[#899480]" /></div>;

  const Section = ({ icon: Icon, title, items, render, testid }) => (
    <div className="mt-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#899480] font-semibold mb-2">
        <Icon size={12} /> {title} ({items.length})
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-[#899480]/70">None yet.</div>
      ) : (
        <div className="space-y-1">{items.map(render)}</div>
      )}
    </div>
  );

  const rowCls = "text-xs bg-[#F8F7F3] rounded-xl px-3 py-2 text-[#3F5043]/80 flex justify-between gap-2";

  return (
    <div className="pt-3 mt-3 border-t border-[#BCB3A6]/30 grid grid-cols-1 md:grid-cols-2 gap-x-6" data-testid={`client-history-${email}`}>
      <div>
        <Section icon={CalendarDays} title="Appointments" items={data.appointments} testid="history-appointments"
          render={(a) => (
            <div key={a.id} className={rowCls}>
              <span>{a.title}</span><span className="text-[#899480]">{fmtDate(a.date)} · {a.status || "scheduled"}</span>
            </div>
          )} />
        <Section icon={NotebookPen} title="Consulting notes" items={data.consulting_notes} testid="history-notes"
          render={(n) => (
            <div key={n.id} className={rowCls}>
              <span>Session note {n.sections?.session_info?.date ? `· ${n.sections.session_info.date}` : ""}</span>
              <span className="text-[#899480]">{n.status}</span>
            </div>
          )} />
        <Section icon={FileText} title="Admin notes" items={data.admin_notes} testid="history-admin-notes"
          render={(n) => (
            <div key={n.id} className={rowCls}><span>{n.content}</span></div>
          )} />
      </div>
      <div>
        <Section icon={CreditCard} title="Invoices" items={data.invoices} testid="history-invoices"
          render={(i) => (
            <div key={i.id} className={rowCls}>
              <span>{i.invoice_number}</span>
              <span className="text-[#899480]">${i.total?.toFixed(2)} · {i.status}</span>
            </div>
          )} />
        <Section icon={Mail} title="Emails" items={data.emails} testid="history-emails"
          render={(e) => (
            <div key={e.id} className={rowCls}>
              <span className="truncate">{e.subject}</span>
              <span className={e.status === "sent" ? "text-[#899480]" : "text-red-600"}>{e.status}</span>
            </div>
          )} />
      </div>
    </div>
  );
}
